import axios from 'axios';
import { API_CONFIG } from '../config/api.config';
import { ApiResponse, LoginResponse } from '../types/backend.types';

const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: 7000, // 7 second timeout - fast fail on weak signal, let user retry
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('bfa_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Suspended-socket retry: when an iOS PWA is backgrounded its network socket
// is suspended. The first request after you reopen the app fires against that
// stale socket and either times out (axios ECONNABORTED) or fails before the
// server is reached (ERR_NETWORK / "Failed to fetch"), even though wifi is
// fine — a manual refresh works instantly because it re-establishes the
// connection. We mirror the policy in utils/network.utils.ts fetchWithTimeout:
// only idempotent methods auto-retry, so a POST that may have reached the
// server (clock-in/out, ticket submit) never double-fires.
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 500;

api.interceptors.response.use(
  response => response,
  async error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('bfa_token');
      localStorage.removeItem('bfa_user');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    const config = error.config;
    // Bail if we got an HTTP response (4xx/5xx is the server speaking, not a
    // transport failure) or if there's no config to retry against.
    if (!config || error.response) return Promise.reject(error);

    const method = (config.method || 'get').toLowerCase();
    if (method !== 'get' && method !== 'head') return Promise.reject(error);

    const anyConfig = config as any;
    anyConfig.__retryCount = (anyConfig.__retryCount || 0) + 1;
    if (anyConfig.__retryCount > MAX_RETRIES) return Promise.reject(error);

    await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
    return api(config);
  }
);

export const authApi = {
  async login(phone: string, pin: string): Promise<ApiResponse<LoginResponse>> {
    const response = await api.post(API_CONFIG.ENDPOINTS.LOGIN, { phone, pin });
    return response.data;
  },

  async updatePin(currentPin: string, newPin: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.put(API_CONFIG.ENDPOINTS.UPDATE_PIN, { currentPin, newPin });
    return response.data;
  }
};

export default api;
